import { useEffect } from "react";
import { useLocation } from "react-router";

/**
 * On route change: scroll to the #hash target if present, otherwise scroll to
 * the top and move focus to the page's <h1> so keyboard / screen-reader users
 * land at the start of the new content.
 *
 * The browser only auto-scrolls to a #fragment on a full document load, NOT on
 * client-side (pushState) navigation — so in-app links like /services#data
 * need this to scroll manually. The target may be lazy-rendered a few frames
 * after navigation, so we retry until it exists. scrollIntoView respects the
 * global scroll-padding-top, so it lands below the sticky header. The retry
 * budget (~2s of frames) covers a cold lazy-route chunk load on a direct
 * /page#section visit, not just warm in-app navigation.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      let raf = 0;
      const tryScroll = (attempts: number) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView();
        } else if (attempts > 0) {
          raf = requestAnimationFrame(() => tryScroll(attempts - 1));
        }
      };
      tryScroll(120);
      return () => cancelAnimationFrame(raf);
    }

    window.scrollTo({ top: 0, behavior: "auto" });
    const h1 = document.querySelector<HTMLHeadingElement>("main h1");
    if (h1) {
      h1.setAttribute("tabindex", "-1");
      h1.focus({ preventScroll: true });
    }
  }, [pathname, hash]);

  return null;
}
